import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../Api/Api";
import endpoints from "../../../Api/Endpoints";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.msg ||
  error?.message ||
  fallback;

const asArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
  }
  return [];
};

const pickString = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }
  return "";
};

const pickNumber = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const formatDate = (value) => {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getPaginationMeta = (payload = {}, fallbackPage = 1, fallbackLimit = 20) => {
  const pag = payload?.pagination || payload;
  const page = pickNumber(pag?.page, payload?.page, fallbackPage);
  const limit = pickNumber(pag?.limit, payload?.limit, fallbackLimit);
  const total = pickNumber(pag?.total, payload?.total, 0);
  const totalPages = pickNumber(pag?.totalPages, payload?.totalPages, 0) || Math.max(1, Math.ceil(total / limit));
  return { page, limit, total, totalPages };
};

const getImageUrl = (obj = {}) => {
  if (!obj || typeof obj !== "object") return "";

  const direct = pickString(
    obj?.image,
    obj?.imageUrl,
    obj?.image_key_url,
    obj?.imageKeyUrl,
    obj?.avatar,
    obj?.avatarUrl,
    obj?.profileImage,
    obj?.profileImageUrl,
    obj?.profilePic,
    obj?.profile_picture,
    obj?.photo,
    obj?.photoUrl,
    obj?.dp,
    obj?.presignedUrl,
    obj?.signedUrl,
    obj?.url,
    obj?.fileUrl,
    obj?.downloadUrl,
    obj?.viewUrl
  );

  if (direct) return direct;

  const nestedDirect = pickString(
    obj?.image?.url,
    obj?.image?.fileUrl,
    obj?.image?.downloadUrl,
    obj?.image?.viewUrl,
    obj?.image?.signedUrl,
    obj?.image?.presignedUrl,
    obj?.image?.image_key_url,
    obj?.image?.imageKeyUrl,

    obj?.avatar?.url,
    obj?.avatar?.fileUrl,
    obj?.avatar?.downloadUrl,
    obj?.avatar?.viewUrl,
    obj?.avatar?.signedUrl,
    obj?.avatar?.presignedUrl,
    obj?.avatar?.image_key_url,
    obj?.avatar?.imageKeyUrl,

    obj?.profileImage?.url,
    obj?.profileImage?.fileUrl,
    obj?.profileImage?.downloadUrl,
    obj?.profileImage?.viewUrl,
    obj?.profileImage?.signedUrl,
    obj?.profileImage?.presignedUrl,
    obj?.profileImage?.image_key_url,
    obj?.profileImage?.imageKeyUrl,

    obj?.photo?.url,
    obj?.photo?.fileUrl,
    obj?.photo?.downloadUrl,
    obj?.photo?.viewUrl,
    obj?.photo?.signedUrl,
    obj?.photo?.presignedUrl,
    obj?.photo?.image_key_url,
    obj?.photo?.imageKeyUrl
  );

  if (nestedDirect) return nestedDirect;

  return "";
};

const normalizeDocType = (doc, fallbackType = "document") => {
  const raw = String(
    pickString(
      doc?.type,
      doc?.documentType,
      doc?.fileType,
      doc?.category,
      doc?.docType,
      fallbackType
    )
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, "_");

  if (raw.includes("report")) return "report";
  if (raw.includes("prescription")) return "prescription";
  return fallbackType;
};

const normalizePatientPrescriptionRecord = (record) => {
  const patient =
    record?.patient ||
    record?.appUser ||
    record?.user ||
    record?.owner ||
    record?.createdBy ||
    {};

  const patientId = pickString(
    patient?._id,
    patient?.id,
    record?.patientId,
    record?.userId,
    record?.appUserId
  );

  const patientName = pickString(
    record?.patientName,
    patient?.name,
    patient?.fullName,
    patient?.patientName,
    "Patient"
  );

  const location = pickString(
    record?.location,
    patient?.location,
    patient?.city,
    patient?.address,
    patient?.address?.city,
    "-"
  );

  const avatar =
    getImageUrl(record) ||
    getImageUrl(patient) ||
    "";

  const statusRaw = String(
    pickString(
      record?.status,
      record?.verificationStatus,
      record?.reviewStatus,
      record?.approvalStatus,
      "pending"
    )
  ).toLowerCase();

  let status = "Pending";
  if (statusRaw.includes("accept") || statusRaw.includes("approv")) status = "Accepted";
  else if (statusRaw.includes("reject")) status = "Rejected";

  const nestedDocs = asArray(
    record?.items,
    record?.documents,
    record?.files,
    record?.prescriptions,
    record?.prescriptionFiles,
    record?.reports,
    record?.reportFiles,
    record?.medicalReports,
    record?.attachments,
    record?.uploads
  );

  const docs = nestedDocs.length
    ? nestedDocs.map((doc, index) => ({
        id: pickString(doc?._id, doc?.id, `${patientId || "doc"}-${index}`),
        prescriptionId: pickString(record?._id, record?.id, record?.prescriptionId),
        type: normalizeDocType(doc, "document"),
        name: pickString(
          doc?.originalName,
          doc?.fileName,
          doc?.filename,
          doc?.name,
          `Document ${index + 1}`
        ),
        url: pickString(
          doc?.url,
          doc?.fileUrl,
          doc?.downloadUrl,
          doc?.viewUrl,
          doc?.presignedUrl,
          doc?.signedUrl
        ),
        size: pickString(doc?.sizeLabel, doc?.fileSize, doc?.size, "Unknown size"),
        uploadedAt: formatDate(
          pickString(doc?.uploadedAt, doc?.createdAt, record?.createdAt, record?.updatedAt)
        ),
      }))
    : [
        {
          id: pickString(record?._id, record?.id, `${patientId || "doc"}-single`),
          prescriptionId: pickString(record?._id, record?.id, record?.prescriptionId),
          type: normalizeDocType(record, "document"),
          name: pickString(
            record?.originalName,
            record?.fileName,
            record?.filename,
            record?.name,
            "Document"
          ),
          url: pickString(
            record?.url,
            record?.fileUrl,
            record?.downloadUrl,
            record?.viewUrl,
            record?.presignedUrl,
            record?.signedUrl
          ),
          size: pickString(record?.sizeLabel, record?.fileSize, record?.size, "Unknown size"),
          uploadedAt: formatDate(
            pickString(record?.uploadedAt, record?.createdAt, record?.updatedAt)
          ),
        },
      ];

  return {
    id: pickString(record?._id, record?.id, patientId),
    prescriptionId: pickString(record?._id, record?.id, record?.prescriptionId),
    patientId,
    patientName,
    location,
    avatar,
    status,
    docs,
    raw: record,
  };
};

const normalizePatientRow = (item, prescriptionGroups = []) => {
  const patient =
    item?.patient ||
    item?.appUser ||
    item?.user ||
    item?.owner ||
    item ||
    {};

  const id = pickString(
    item?.patientId,
    item?._id,
    item?.id,
    patient?._id,
    patient?.id
  );

  const matched = prescriptionGroups.filter(
    (entry) => String(entry?.patientId || entry?.userId || "") === String(id || "")
  );

  const prescriptionIds = matched.map((entry) => entry.prescriptionId || entry.id).filter(Boolean);
  const docs = matched.flatMap((entry) => entry.docs || []);

  const hasAccepted = matched.some((entry) => entry.status === "Accepted");
  const hasRejected = matched.some((entry) => entry.status === "Rejected");
  const hasPending = matched.some((entry) => entry.status === "Pending");

  let status = "Pending";
  if (hasAccepted) status = "Accepted";
  else if (hasRejected && !hasPending) status = "Rejected";

  const responses = item?.responses || patient?.responses || {};
  return {
    id,
    patientName: pickString(
      item?.patientName,
      item?.name,
      item?.fullName,
      patient?.name,
      patient?.fullName,
      matched?.[0]?.patientName,
      "Unknown"
    ),
    location: pickString(
      item?.location,
      item?.city,
      item?.address,
      patient?.location,
      patient?.city,
      "-"
    ),
    image:
      getImageUrl(item) ||
      getImageUrl(patient) ||
      pickString(item?.avatar, patient?.avatar) ||
      matched?.[0]?.avatar ||
      "",
    healthRequest: pickString(
      item?.healthRequestsSummary,
      item?.healthRequest,
      item?.requestTitle,
      item?.subcategoryName,
      item?.categoryName,
      "Medical Request"
    ),
    dateCreated: pickString(
      item?.dateCreatedFormatted,
      item?.dateCreated,
      formatDate(item?.createdAt || item?.updatedAt || matched?.[0]?.raw?.createdAt)
    ),
    responsesCount: pickNumber(
      responses?.totalBids,
      item?.responsesCount,
      item?.bidsCount,
      item?.providerCount,
      0
    ),
    acceptedCount: pickNumber(
      responses?.accepted,
      item?.acceptedCount,
      item?.acceptedProviders,
      0
    ),
    status,
    prescriptionIds,
    docs,
    raw: item,
  };
};

const normalizeDoctorRow = (item) => {
  const doctor = item?.doctor || item?.provider || item || {};
  const responses = item?.responses || doctor?.responses || {};
  const rawStatus = String(
    pickString(item?.status, item?.verificationStatus, item?.approvalStatus, "pending")
  ).toLowerCase();

  return {
    id: pickString(item?.doctorId, item?._id, item?.id, doctor?._id, doctor?.id),
    providerType: "doctor",
    name: pickString(
      item?.doctorName,
      item?.name,
      item?.fullName,
      doctor?.name,
      doctor?.fullName,
      "Unknown"
    ),
    image: getImageUrl(item) || getImageUrl(doctor) || pickString(item?.image, doctor?.image) || "",
    specialty: pickString(
      item?.specialties,
      item?.specialty,
      item?.specialization,
      item?.speciality,
      doctor?.specialty,
      doctor?.specialization,
      "Doctor"
    ),
    location: pickString(
      item?.location,
      item?.city,
      item?.cityOfPractice,
      item?.address,
      doctor?.location,
      doctor?.city,
      doctor?.cityOfPractice,
      "-"
    ),
    dateCreated: pickString(
      item?.dateCreatedFormatted,
      item?.dateCreated,
      formatDate(item?.createdAt || item?.updatedAt || doctor?.createdAt)
    ),
    responsesCount: pickNumber(
      responses?.totalBids,
      item?.responsesCount,
      item?.bidsCount,
      item?.providerCount,
      0
    ),
    acceptedCount: pickNumber(
      responses?.accepted,
      item?.acceptedCount,
      item?.acceptedProviders,
      item?.approvedCount,
      0
    ),
    status:
      rawStatus.includes("accept") || rawStatus.includes("approv")
        ? "Accepted"
        : rawStatus.includes("reject")
        ? "Rejected"
        : "Pending",
    raw: item,
  };
};

const normalizeHospitalRow = (item) => {
  const hospital = item?.hospital || item?.provider || item || {};
  const responses = item?.responses || hospital?.responses || {};
  const rawStatus = String(
    pickString(item?.status, item?.verificationStatus, item?.approvalStatus, "pending")
  ).toLowerCase();

  return {
    id: pickString(item?.hospitalId, item?._id, item?.id, hospital?._id, hospital?.id),
    providerType: "hospital",
    name: pickString(
      item?.hospitalName,
      item?.name,
      hospital?.name,
      hospital?.hospitalName,
      "Unknown"
    ),
    image: getImageUrl(item) || getImageUrl(hospital) || pickString(item?.avatar, hospital?.avatar) || "",
    specialty: pickString(
      item?.hospitalType,
      item?.specialty,
      item?.serviceName,
      hospital?.hospitalType,
      "Hospital"
    ),
    location: pickString(
      item?.location,
      item?.city,
      item?.address,
      hospital?.location,
      hospital?.city,
      hospital?.state,
      "-"
    ),
    dateCreated: pickString(
      item?.dateCreatedFormatted,
      item?.dateCreated,
      formatDate(item?.createdAt || item?.updatedAt || hospital?.createdAt)
    ),
    responsesCount: pickNumber(
      responses?.totalBids,
      item?.responsesCount,
      item?.bidsCount,
      item?.providerCount,
      0
    ),
    acceptedCount: pickNumber(
      responses?.accepted,
      item?.acceptedCount,
      item?.acceptedProviders,
      item?.approvedCount,
      0
    ),
    status:
      rawStatus.includes("accept") || rawStatus.includes("approv")
        ? "Accepted"
        : rawStatus.includes("reject")
        ? "Rejected"
        : "Pending",
    raw: item,
  };
};

export const getAdminBidsPageData = createAsyncThunk(
  "bids/getAdminBidsPageData",
  async (
    { activeTab = "patient", page = 1, limit = 20, lastDays = 30 } = {},
    { rejectWithValue }
  ) => {
    try {
      const prescriptionsRes = await API.get(endpoints.ADMIN_PRESCRIPTIONS, {
        params: { page: 1, limit: 300 },
      });

      const prescriptionsPayload = prescriptionsRes?.data || {};
      const prescriptionGroups = asArray(
        prescriptionsPayload?.items,
        prescriptionsPayload?.data,
        prescriptionsPayload?.rows
      ).map(normalizePatientPrescriptionRecord);

      if (activeTab === "patient") {
        const patientsRes = await API.get(endpoints.ADMIN_PATIENTS, {
          params: { page, limit, lastDays },
        });

        const patientsPayload = patientsRes?.data || {};
        const patients = asArray(
          patientsPayload?.items,
          patientsPayload?.data,
          patientsPayload?.rows
        ).map((item) => normalizePatientRow(item, prescriptionGroups));

        return {
          activeTab,
          patients,
          providers: [],
          pagination: {
            patient: getPaginationMeta(patientsPayload, page, limit),
            provider: { page: 1, limit, total: 0, totalPages: 1 },
          },
        };
      }

      const [doctorsRes, hospitalsRes] = await Promise.all([
        API.get(endpoints.ADMIN_DOCTORS_LIST, {
          params: { page, limit, lastDays },
        }),
        API.get(endpoints.ADMIN_HOSPITALS_LIST, {
          params: { page, limit, lastDays },
        }),
      ]);

      const doctorsPayload = doctorsRes?.data || {};
      const hospitalsPayload = hospitalsRes?.data || {};

      const doctors = asArray(
        doctorsPayload?.items,
        doctorsPayload?.data,
        doctorsPayload?.rows
      ).map(normalizeDoctorRow);

      const hospitals = asArray(
        hospitalsPayload?.items,
        hospitalsPayload?.data,
        hospitalsPayload?.rows
      ).map(normalizeHospitalRow);

      const providers = [...doctors, ...hospitals];

      const doctorMeta = getPaginationMeta(doctorsPayload, page, limit);
      const hospitalMeta = getPaginationMeta(hospitalsPayload, page, limit);

      return {
        activeTab,
        patients: [],
        providers,
        pagination: {
          patient: { page: 1, limit, total: 0, totalPages: 1 },
          provider: {
            page,
            limit,
            total: doctorMeta.total + hospitalMeta.total,
            totalPages: Math.max(doctorMeta.totalPages, hospitalMeta.totalPages, 1),
          },
        },
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load bids page data"));
    }
  }
);

export const updatePatientPrescriptionStatus = createAsyncThunk(
  "bids/updatePatientPrescriptionStatus",
  async ({ patientId, prescriptionIds = [], action }, { rejectWithValue }) => {
    try {
      if (!prescriptionIds.length) {
        return rejectWithValue("No prescription IDs found for this patient");
      }

      let res;

      if (action === "accept") {
        res = await API.post(endpoints.ADMIN_ACCEPT_MULTIPLE_PRESCRIPTIONS, {
          prescriptionIds,
        });
      } else {
        res = await API.post(endpoints.ADMIN_REJECT_MULTIPLE_PRESCRIPTIONS, {
          prescriptionIds,
          rejectionReason: "Rejected by admin",
        });
      }

      return {
        patientId,
        prescriptionIds,
        action,
        payload: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update patient status"));
    }
  }
);

export const getProviderDetails = createAsyncThunk(
  "bids/getProviderDetails",
  async ({ providerId, providerType }, { rejectWithValue }) => {
    try {
      const endpoint =
        providerType === "hospital"
          ? endpoints.adminHospitalById(providerId)
          : endpoints.adminDoctorById(providerId);

      const res = await API.get(endpoint);

      return {
        providerId,
        providerType,
        details: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load provider details"));
    }
  }
);

export const updateProviderStatus = createAsyncThunk(
  "bids/updateProviderStatus",
  async ({ providerId, providerType, action }, { rejectWithValue }) => {
    try {
      const endpoint =
        providerType === "hospital"
          ? action === "accept"
            ? endpoints.adminHospitalApprove(providerId)
            : endpoints.adminHospitalReject(providerId)
          : action === "accept"
          ? endpoints.adminDoctorApprove(providerId)
          : endpoints.adminDoctorReject(providerId);

      const res = await API.post(endpoint);

      return {
        providerId,
        providerType,
        action,
        payload: res?.data || {},
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update provider status"));
    }
  }
);

/**
 * Fetch prescriptions and reports for a patient by userId (for View modal).
 * GET /admin/prescriptions?userId=patientId
 */
export const getPrescriptionsByPatientId = createAsyncThunk(
  "bids/getPrescriptionsByPatientId",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await API.get(endpoints.ADMIN_PRESCRIPTIONS, {
        params: { userId, page: 1, limit: 100 },
      });
      const list = asArray(res?.data?.data, res?.data?.items, res?.data?.rows);
      return { userId, list };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to load patient documents"));
    }
  }
);
